import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import {
  usePermissionModules,
  useResetRolePermissions,
  useRolePermissions,
  useSaveRolePermissions,
} from "../../api/queries/adminToolsQueries";
import usePermissions from "../../hooks/usePermissions";
import { getApiErrorMessage } from "../../utils/apiError";
import "./admin.css";

const EMPTY = [];
const SCOPE_LABEL = { COMPANY: "Whole company", BRANCH: "Own branches" };
// Which employees the module covers: "" = all, else only that assignment type
const TYPE_LABEL = {
  "": "All employees",
  Internal: "Internal only",
  Deployed: "Deployed only",
};

/** Modules × View / Modify / Scope for one role. Modify implies View. */
const matrixOf = (role, modules) =>
  Object.fromEntries(
    modules.map((m) => {
      const p = role?.permissions?.[m.key] || {};
      return [
        m.key,
        {
          view: !!(p.view || p.modify),
          modify: !!p.modify,
          // No saved row yet (e.g. a module added later): shown as "Not set"
          isSet: !!role?.permissions?.[m.key],
          scope: p.scope || "COMPANY",
          employeeType: p.employeeType || "",
        },
      ];
    }),
  );

const PermissionDialog = ({
  role,
  roles,
  modules,
  scopes,
  employeeTypes,
  onClose,
}) => {
  const [rows, setRows] = useState(() => matrixOf(role, modules));
  // Copy another role's matrix into the form (nothing is saved until Save)
  const copySources = roles.filter((r) => r.id !== role.id && r.configured);
  const copyFrom = (id) => {
    const src = roles.find((r) => r.id === id);
    if (!src) return;
    setRows(matrixOf(src, modules));
    toast.info(`Copied from ${src.title}. Review, then Save.`);
  };
  const save = useSaveRolePermissions();
  const reset = useResetRolePermissions();
  const busy = save.isPending || reset.isPending;
  const set = (key, changes) =>
    setRows((r) => {
      const next = { ...r[key], ...changes };
      if (changes.modify) next.view = true; // modify implies view
      if (changes.view === false) next.modify = false;
      return { ...r, [key]: next };
    });
  // The scope most granted modules use; others are flagged so a stray
  // "Own branches" (or "Whole company") is easy to spot
  const scopeCounts = Object.values(rows).reduce((acc, v) => {
    if (v.view) acc[v.scope] = (acc[v.scope] || 0) + 1;
    return acc;
  }, {});
  const usualScope = Object.entries(scopeCounts).sort(
    (a, b) => b[1] - a[1],
  )[0]?.[0];
  const mixedScopes = Object.keys(scopeCounts).length > 1;
  const sameScopeForAll = (scope) =>
    setRows((r) =>
      Object.fromEntries(
        Object.entries(r).map(([k, v]) => [k, v.view ? { ...v, scope } : v]),
      ),
    );
  const allOn = (field, on) =>
    setRows((r) =>
      Object.fromEntries(
        Object.entries(r).map(([k, v]) => [
          k,
          field === "modify"
            ? { ...v, modify: on, view: on || v.view }
            : { ...v, view: on, modify: on ? v.modify : false },
        ]),
      ),
    );

  const submit = () =>
    save.mutate(
      {
        roleId: role.id,
        permissions: Object.entries(rows)
          .filter(([, v]) => v.view || v.modify)
          .map(([module, v]) => ({
            module,
            canView: true,
            canModify: v.modify,
            scope: v.scope,
            employeeType: v.employeeType || null,
          })),
      },
      {
        onSuccess: () => {
          toast.success(`Permissions saved for ${role.title}.`);
          onClose();
        },
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, "Could not save the permissions."),
          ),
      },
    );
  const doReset = () => {
    if (
      !window.confirm(
        `Reset ${role.title}? It loses all HR-portal access until it is set up again.`,
      )
    )
      return;
    reset.mutate(role.id, {
      onSuccess: () => {
        toast.success(`${role.title} setup removed.`);
        onClose();
      },
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not reset the role.")),
    });
  };

  return (
    <Dialog
      title={`Permissions: ${role.title}`}
      subtitle={
        role.company?.companyName
          ? role.company.companyName
          : "Shared role (all companies)"
      }
      size="lg"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          {role.configured && (
            <button
              type="button"
              className="btn btn-outline-danger profile-btn me-auto"
              onClick={doReset}
              disabled={busy}
            >
              Remove setup
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={busy}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save
          </button>
        </>
      }
    >
      <p className="fs-080 text-slate-600">
        Modules left unticked = no access. <strong>Whole company</strong> is
        HR-level (company and subsidiaries; needed for setup, master data and
        company-wide screens); <strong>Own branches</strong> gives
        branch-manager rules (only the branches the user manages). Role names
        grant nothing. Employee pickers in other modules need{" "}
        <em>Employees: View</em>.
      </p>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {copySources.length > 0 && (
          <select
            className="form-select form-select-sm w-auto"
            value=""
            onChange={(e) => copyFrom(e.target.value)}
            aria-label="Copy from role"
            disabled={busy}
          >
            <option value="">Copy from role…</option>
            {copySources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title}
                {r.company?.companyName ? ` (${r.company.companyName})` : ""}
              </option>
            ))}
          </select>
        )}
        <select
          className="form-select form-select-sm w-auto"
          value=""
          onChange={(e) => e.target.value && sameScopeForAll(e.target.value)}
          aria-label="Same scope for all modules"
        >
          <option value="">Same scope for all…</option>
          {scopes.map((sc) => (
            <option key={sc} value={sc}>
              {SCOPE_LABEL[sc] || sc}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-sm btn-light border"
          onClick={() => allOn("view", true)}
        >
          View all
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light border"
          onClick={() => allOn("modify", true)}
        >
          Modify all
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light border"
          onClick={() => allOn("view", false)}
        >
          Clear
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-sm mb-0 perm-matrix">
          <thead>
            <tr>
              <th>Module</th>
              <th className="text-center">View</th>
              <th className="text-center">Modify</th>
              <th>Scope</th>
              <th>Employees</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => {
              const r = rows[m.key];
              const differs = mixedScopes && r.view && r.scope !== usualScope;
              return (
                <tr key={m.key} className={differs ? "perm-differs" : ""}>
                  <td>
                    <div className="fw-semibold">
                      {m.label}
                      {!r.isSet && !r.view && (
                        <span className="chip chip--sm chip--alt ms-2">
                          Not set
                        </span>
                      )}
                      {differs && (
                        <span
                          className="status-pill status-pill--amber ms-2"
                          title="This module's scope is different from the rest of the role"
                        >
                          {SCOPE_LABEL[r.scope] || r.scope}: differs
                        </span>
                      )}
                    </div>
                    {m.description && (
                      <div className="fs-070 text-slate-500">
                        {m.description}
                      </div>
                    )}
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={r.view}
                      onChange={(e) => set(m.key, { view: e.target.checked })}
                      aria-label={`${m.label}: view`}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={r.modify}
                      onChange={(e) => set(m.key, { modify: e.target.checked })}
                      aria-label={`${m.label}: modify`}
                    />
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm perm-scope"
                      value={r.scope}
                      onChange={(e) => set(m.key, { scope: e.target.value })}
                      disabled={!r.view}
                      aria-label={`${m.label}: scope`}
                    >
                      {scopes.map((s) => (
                        <option key={s} value={s}>
                          {SCOPE_LABEL[s] || s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm perm-scope"
                      value={r.employeeType}
                      onChange={(e) =>
                        set(m.key, { employeeType: e.target.value })
                      }
                      disabled={!r.view}
                      aria-label={`${m.label}: employees`}
                      title="Limit this module to internal or deployed employees"
                    >
                      {employeeTypes.map((t) => (
                        <option key={t || "all"} value={t || ""}>
                          {TYPE_LABEL[t || ""] || t}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Dialog>
  );
};

/** Admins (company-setup, COMPANY scope): what each role can view or change. Roles without setup have no HR-portal access. */
const RolePermissionsPage = () => {
  const { companyWide, superAdmin } = usePermissions();
  const rolesQuery = useRolePermissions();
  const modulesQuery = usePermissionModules();
  const modules = modulesQuery.data?.modules || EMPTY;
  const scopes = modulesQuery.data?.scopes || ["COMPANY", "BRANCH"];
  const employeeTypes = modulesQuery.data?.employeeTypes || [
    null,
    "Internal",
    "Deployed",
  ];
  const labelOf = useMemo(
    () => new Map(modules.map((m) => [m.key, m.label])),
    [modules],
  );
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const canEdit = companyWide("company-setup", "modify");
  const roles = (rolesQuery.data || EMPTY).filter(
    (r) =>
      !search.trim() ||
      (r.title || "").toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">
              Roles & Permissions
            </h1>
            <p className="text-muted small mb-0">
              Per role: which modules it can view or change, and in which scope.
            </p>
          </div>
          <Link to="/role" className="btn profile-btn profile-btn-white">
            <i className="bi bi-list-ul"></i> Manage roles
          </Link>
        </div>

        <div className="alert alert-warning fs-085 py-2">
          <i className="bi bi-exclamation-triangle me-1"></i>
          Roles without any setup cannot use the HR portal. Set up HR/Admin
          roles first.
        </div>

        <div className="mb-3">
          <input
            type="search"
            className="filter-input"
            placeholder="Search roles"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search roles"
          />
        </div>

        <div className="glass-table-card">
          {rolesQuery.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(rolesQuery.error, "Could not load roles.")}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 admin-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Role</th>
                    <th className="py-3">Company</th>
                    <th className="py-3">Access</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {roles.map((r) => {
                    const entries = Object.entries(r.permissions || {}).filter(
                      ([, p]) => p.view || p.modify,
                    );
                    const shared = !r.company;
                    return (
                      <tr
                        key={r.id}
                        className={
                          r.isActive === false ? "admin-row-inactive" : ""
                        }
                      >
                        <td className="ps-4 fw-semibold">{r.title}</td>
                        <td>
                          {shared ? (
                            <span className="text-slate-500">Shared</span>
                          ) : (
                            r.company?.companyName
                          )}
                        </td>
                        <td>
                          {!r.configured ? (
                            <span
                              className="status-pill status-pill--red"
                              title="No HR-portal access until set up"
                            >
                              Not set up
                            </span>
                          ) : entries.length === 0 ? (
                            <span className="status-pill status-pill--red">
                              No access
                            </span>
                          ) : (
                            <div className="perm-chips">
                              {entries.map(([key, p]) => (
                                <span
                                  key={key}
                                  className={`chip chip--sm ${p.modify ? "" : "chip--alt"}`}
                                  title={`${p.modify ? "View + modify" : "View only"} · ${SCOPE_LABEL[p.scope] || p.scope} · ${TYPE_LABEL[p.employeeType || ""]}`}
                                >
                                  {labelOf.get(key) || key}
                                  {p.modify ? "" : " (view)"}
                                  {p.scope === "BRANCH" ? " · branch" : ""}
                                  {p.employeeType
                                    ? ` · ${p.employeeType.toLowerCase()}`
                                    : ""}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="pe-4 text-end">
                          {canEdit && (!shared || superAdmin) ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-light border att-action-btn"
                              onClick={() => setEditing(r)}
                              disabled={!modules.length}
                            >
                              <i className="bi bi-pencil"></i> Edit
                            </button>
                          ) : (
                            shared && (
                              <span
                                className="fs-070 text-slate-400"
                                title="Shared roles can only be changed by the super admin"
                              >
                                super admin only
                              </span>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!rolesQuery.isLoading && roles.length === 0 && (
                    <tr>
                      <td colSpan={4} className="approval-empty">
                        No roles.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {rolesQuery.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {editing && (
        <PermissionDialog
          role={editing}
          roles={rolesQuery.data || EMPTY}
          modules={modules}
          scopes={scopes}
          employeeTypes={employeeTypes}
          onClose={() => setEditing(null)}
        />
      )}
    </Layout>
  );
};

export default RolePermissionsPage;
